'use client';

import { useState, useEffect } from 'react';

interface Line {
  id: string;
  text: string;
  english?: string;
  translation?: string;
  Ang?: string;
}

interface DisplayState {
  mode: 'shabad' | 'custom' | 'blank';
  currentLineIndex: number;
  lines: Line[];
  customSlide?: {
    title: string;
    body: string;
    useGurmukhi: boolean;
  };
}

interface DisplayContentProps {
  className?: string;
}


const DisplayContent = ({ className = '' }: DisplayContentProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [displayState, setDisplayState] = useState<DisplayState>({
    mode: 'blank',
    currentLineIndex: 0,
    lines: [],
  });

  // Helper to update state and sync to localStorage
  const updateCurrentLineIndex = (newIndex: number) => {
    setDisplayState(prev => {
      const updated = { ...prev, currentLineIndex: newIndex };
      try {
        localStorage.setItem('gurbani-display-state', JSON.stringify(updated));
      } catch (e) {
        // ignore
      }
      return updated;
    });
  };

  // Keyboard navigation
  useEffect(() => {
    if (!isHydrated) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (displayState.mode !== 'shabad' || displayState.lines.length === 0) return;
      if (
        e.key === 'ArrowDown' ||
        e.key === 'ArrowRight'
      ) {
        // Next line
        if (displayState.currentLineIndex < displayState.lines.length - 1) {
          updateCurrentLineIndex(displayState.currentLineIndex + 1);
        }
      } else if (
        e.key === 'ArrowUp' ||
        e.key === 'ArrowLeft'
      ) {
        // Previous line
        if (displayState.currentLineIndex > 0) {
          updateCurrentLineIndex(displayState.currentLineIndex - 1);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isHydrated, displayState]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    // Load initial display state from localStorage
    const loadDisplayState = () => {
      try {
        const savedState = localStorage.getItem('gurbani-display-state');
        if (savedState) {
          const state = JSON.parse(savedState);
          setDisplayState(state);
        } else {
          // If display state is missing, set to blank
          setDisplayState({ mode: 'blank', currentLineIndex: 0, lines: [] });
        }
      } catch (error) {
        console.error('Error loading display state:', error);
        setDisplayState({ mode: 'blank', currentLineIndex: 0, lines: [] });
      }
    };

    // Listen for storage changes (when controller updates it)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'gurbani-display-state' && e.newValue) {
        try {
          const state = JSON.parse(e.newValue);
          setDisplayState(state);
        } catch (error) {
          console.error('Error parsing display state:', error);
        }
      }
    };

    // Load initial state
    loadDisplayState();

    // Listen for storage changes from other tabs/windows
    window.addEventListener('storage', handleStorageChange);

    // Also poll localStorage in case both windows are open in same browser
    const pollInterval = setInterval(loadDisplayState, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(pollInterval);
    };
  }, [isHydrated]);

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/projector-display/components/DisplayContent.tsx:130:6" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="130" data-component-end-line="132" data-component-file="DisplayContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`min-h-screen bg-black flex items-center justify-center ${className}`}>
        <div data-component-id="src/app/projector-display/components/DisplayContent.tsx:131:8" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="131" data-component-end-line="131" data-component-file="DisplayContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-white%2F20%20text-2xl%22%2C%22textContent%22%3A%22Loading%20Display...%22%7D" className="text-white/20 text-2xl">Loading Display...</div>
      </div>
    );
  }

  // Blank/Waiting State
  if (displayState.mode === 'blank' || displayState.lines.length === 0) {
    return (
      <div data-component-id="src/app/projector-display/components/DisplayContent.tsx:139:6" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="139" data-component-end-line="144" data-component-file="DisplayContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`min-h-screen bg-black flex items-center justify-center ${className}`}>
        <div data-component-id="src/app/projector-display/components/DisplayContent.tsx:140:8" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="140" data-component-end-line="143" data-component-file="DisplayContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20space-y-4%22%7D" className="text-center space-y-4">
          <div data-component-id="src/app/projector-display/components/DisplayContent.tsx:141:10" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="141" data-component-end-line="141" data-component-file="DisplayContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-16%20h-16%20mx-auto%20border-4%20border-white%2F10%20border-t-white%2F30%20rounded-full%20animate-spin%22%7D" className="w-16 h-16 mx-auto border-4 border-white/10 border-t-white/30 rounded-full animate-spin" />
          <p data-component-id="src/app/projector-display/components/DisplayContent.tsx:142:10" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="142" data-component-end-line="142" data-component-file="DisplayContent.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-white%2F30%20text-xl%22%2C%22textContent%22%3A%22Waiting%20for%20content%20from%20Presenter...%22%7D" className="text-white/30 text-xl">Waiting for content from Presenter...</p>
        </div>
      </div>
    );
  }

  // Custom Slide Mode
  if (displayState.mode === 'custom' && displayState.customSlide) {
    return (
      <div data-component-id="src/app/projector-display/components/DisplayContent.tsx:151:6" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="151" data-component-end-line="168" data-component-file="DisplayContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`min-h-screen bg-black flex items-center justify-center p-12 ${className}`}>
        <div data-component-id="src/app/projector-display/components/DisplayContent.tsx:152:8" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="152" data-component-end-line="167" data-component-file="DisplayContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-5xl%20w-full%20text-center%20space-y-8%22%7D" className="max-w-5xl w-full text-center space-y-8">
          <h1 data-component-id="src/app/projector-display/components/DisplayContent.tsx:153:10" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="153" data-component-end-line="159" data-component-file="DisplayContent.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%7D"
            className={`text-6xl font-bold text-white mb-8 ${
              displayState.customSlide.useGurmukhi ? 'font-gurmukhi' : ''
            }`}
          >
            {displayState.customSlide.title}
          </h1>
          <div data-component-id="src/app/projector-display/components/DisplayContent.tsx:160:10" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="160" data-component-end-line="166" data-component-file="DisplayContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
            className={`text-3xl text-white/90 leading-relaxed whitespace-pre-wrap ${
              displayState.customSlide.useGurmukhi ? 'font-gurmukhi' : ''
            }`}
          >
            {displayState.customSlide.body}
          </div>
        </div>
      </div>
    );
  }

  // Shabad Mode
  const safeLineIndex = Math.max(0, Math.min(displayState.currentLineIndex, displayState.lines.length - 1));
  const currentLine = displayState.lines?.[safeLineIndex] ?? { id: 'empty', text: 'No content available', english: '', translation: '', Ang: '' };
  const previousLine =
    safeLineIndex > 0 ? displayState.lines?.[safeLineIndex - 1] : null;
  const nextLine =
    safeLineIndex < (displayState.lines?.length ?? 0) - 1
      ? displayState.lines?.[safeLineIndex + 1]
      : null;

  return (
    <div data-component-id="src/app/projector-display/components/DisplayContent.tsx:183:4" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="183" data-component-end-line="232" data-component-file="DisplayContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`min-h-screen bg-black flex items-center justify-center p-8 ${className}`}>
      <div data-component-id="src/app/projector-display/components/DisplayContent.tsx:184:6" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="184" data-component-end-line="231" data-component-file="DisplayContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-6xl%20w-full%20space-y-6%22%7D" className="max-w-6xl w-full space-y-6">
        {/* Previous Line - Dimmed */}
        {previousLine && (
          <div data-component-id="src/app/projector-display/components/DisplayContent.tsx:187:10" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="187" data-component-end-line="195" data-component-file="DisplayContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20opacity-30%20transition-all%20duration-300%22%7D" className="text-center opacity-30 transition-all duration-300">
            <p data-component-id="src/app/projector-display/components/DisplayContent.tsx:188:12" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="188" data-component-end-line="188" data-component-file="DisplayContent.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-3xl%20text-white%20font-gurmukhi%22%7D" className="text-3xl text-white font-gurmukhi">{previousLine.text}</p>
            {previousLine.english && (
              <p data-component-id="src/app/projector-display/components/DisplayContent.tsx:190:14" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="190" data-component-end-line="190" data-component-file="DisplayContent.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xl%20text-white%2F90%20mt-2%22%7D" className="text-xl text-white/90 mt-2">{previousLine.english}</p>
            )}
            {previousLine.translation && (
              <p data-component-id="src/app/projector-display/components/DisplayContent.tsx:193:14" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="193" data-component-end-line="193" data-component-file="DisplayContent.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xl%20text-white%2F80%20mt-2%22%7D" className="text-xl text-white/80 mt-2">{previousLine.translation}</p>
            )}
          </div>
        )}

        {/* Current Line - Highlighted */}
        <div data-component-id="src/app/projector-display/components/DisplayContent.tsx:199:8" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="199" data-component-end-line="209" data-component-file="DisplayContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-8%20px-6%20bg-white%2F5%20rounded-2xl%20border-2%20border-white%2F20%20shadow-2xl%20transition-all%20duration-300%22%7D" className="text-center py-8 px-6 bg-white/5 rounded-2xl border-2 border-white/20 shadow-2xl transition-all duration-300">
          <p data-component-id="src/app/projector-display/components/DisplayContent.tsx:200:10" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="200" data-component-end-line="202" data-component-file="DisplayContent.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-6xl%20text-amber-300%20font-gurmukhi%20leading-relaxed%20mb-6%22%7D" className="text-6xl text-amber-300 font-gurmukhi leading-relaxed mb-6">
            {currentLine.text}
          </p>
          {currentLine.english && (
            <p data-component-id="src/app/projector-display/components/DisplayContent.tsx:204:12" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="204" data-component-end-line="204" data-component-file="DisplayContent.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-3xl%20text-cyan-300%20leading-relaxed%20mb-4%22%7D" className="text-3xl text-cyan-300 leading-relaxed mb-4">{currentLine.english}</p>
          )}
          {currentLine.translation && (
            <p data-component-id="src/app/projector-display/components/DisplayContent.tsx:207:12" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="207" data-component-end-line="207" data-component-file="DisplayContent.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-3xl%20text-white%2F90%20leading-relaxed%22%7D" className="text-3xl text-white/90 leading-relaxed">{currentLine.translation}</p>
          )}
        </div>

        {/* Next Line - Dimmed */}
        {nextLine && (
          <div data-component-id="src/app/projector-display/components/DisplayContent.tsx:213:10" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="213" data-component-end-line="221" data-component-file="DisplayContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20opacity-30%20transition-all%20duration-300%22%7D" className="text-center opacity-30 transition-all duration-300">
            <p data-component-id="src/app/projector-display/components/DisplayContent.tsx:214:12" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="214" data-component-end-line="214" data-component-file="DisplayContent.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-3xl%20text-white%20font-gurmukhi%22%7D" className="text-3xl text-white font-gurmukhi">{nextLine.text}</p>
            {nextLine.english && (
              <p data-component-id="src/app/projector-display/components/DisplayContent.tsx:216:14" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="216" data-component-end-line="216" data-component-file="DisplayContent.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xl%20text-white%2F90%20mt-2%22%7D" className="text-xl text-white/90 mt-2">{nextLine.english}</p>
            )}
            {nextLine.translation && (
              <p data-component-id="src/app/projector-display/components/DisplayContent.tsx:219:14" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="219" data-component-end-line="219" data-component-file="DisplayContent.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xl%20text-white%2F80%20mt-2%22%7D" className="text-xl text-white/80 mt-2">{nextLine.translation}</p>
            )}
          </div>
        )}

        {/* Line Counter */}
        <div data-component-id="src/app/projector-display/components/DisplayContent.tsx:225:8" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="225" data-component-end-line="230" data-component-file="DisplayContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mt-8%22%7D" className="text-center mt-8">
          <p data-component-id="src/app/projector-display/components/DisplayContent.tsx:226:10" data-component-path="src/app/projector-display/components/DisplayContent.tsx" data-component-line="226" data-component-end-line="229" data-component-file="DisplayContent.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-white%2F40%20text-lg%22%2C%22textContent%22%3A%22Line%20of%22%7D" className="text-white/40 text-lg">
            Line {displayState.currentLineIndex + 1} of {displayState.lines.length}
            {currentLine.Ang ? ` • Ang ${currentLine.Ang}` : ''}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DisplayContent;
